import { INetworkMidLevelMadlibModel } from './../network-mid-level/network-mid-level.models';
import { SpinnerModal } from 'Src/app/shared/modals/status-spinner/status-spinner.service';
import { ObjectCache } from 'Src/ng2/shared/services/object-cache/object-cache.service';
import { IStateService, IStateParamsService } from 'angular-ui-router';
import { sortBy } from 'lodash';
import { SortAndFilterService } from '../../shared/services/list-services/sort-and-filter.service';
import { Observable } from 'rxjs/Observable';
import { switchMap, tap, startWith, take } from 'rxjs/operators';
import { NetDashTileDataService, ITileData } from './net-dash-tile-data/net-dash-tile-data.service';
import { FormControl } from '@angular/forms';
import { Component, OnInit, Inject, Input } from '@angular/core';
import { combineLatest, Observer } from 'rxjs';

export type TValidTileSchoolTypes = 'ALL' | 'hs' | 'hst' | 'ems';

export interface ITileSchoolTypeMadlibOptions {
  key: string;
  human: string;
  // Used to decide which option should be set as the initial madlib selection on load
  defaultHierarchy: number;
}

@Component({
  selector: 'network-top-level',
  template: require('./network-top-level.component.html'),
  styles: [require('./network-top-level.component.scss')],
})
export class NetworkTopLevelComponent implements OnInit {
  @Input() filters: { search: string; madlibModel: INetworkMidLevelMadlibModel };
  // TILES
  public tileData$: Observable<Array<ITileData>>;
  public filteredTileData$: Observable<Array<ITileData>>;
  public tileCategories$: Observable<Array<string>>;

  // MADLIB
  public filterFormControl: FormControl;
  public madlibModel: FormControl = new FormControl();
  public madlibOptions: Array<ITileSchoolTypeMadlibOptions>;
  public madlibData: { madlibOptions: Array<ITileSchoolTypeMadlibOptions>; madlibModel: FormControl };

  // UI SPECIFICS
  public panelShadowStyle: boolean = false;

  constructor(
    private tileDataService: NetDashTileDataService,
    @Inject('$state') public $state: IStateService,
    @Inject('$stateParams') public $stateParams: IStateParamsService,
    @Inject('SpinnerModal') public spinnerService: SpinnerModal,
    private objectCache: ObjectCache,
  ) {}

  ngOnInit(): void {
    this.filterFormControl = new FormControl(this.filters ? this.filters.search : '');
    this.tileData$ = this.tileDataService.getNetDashTileData$().pipe(
      tap(tileData => {
        const tileSchoolTypes = this.getTileSchoolTypes(tileData);
        this.madlibOptions = this.getMadlibOptions(tileSchoolTypes);
        this.madlibModel.setValue(this.filters ? this.filters.madlibModel : this.getMadlibModel(this.madlibOptions));
        this.madlibData = { madlibOptions: this.madlibOptions, madlibModel: this.madlibModel };
        this.spinnerService.deactivateSpinner();
      }),
    );

    this.filteredTileData$ = combineLatest(
      this.tileData$,
      this.filterFormControl.valueChanges.pipe(startWith(this.filters ? this.filters.search : '')),
      this.madlibModel.valueChanges,
    ).pipe(
      tap(([_, search, madlibSelection]) => this.updateUrl(search, madlibSelection)),
      switchMap(([tileData, search, madlibSelection]) => this.getFilteredTilesData$(tileData, search, madlibSelection)),
    );
  }

  public setPanelShadowStyle(scrolledOutOfView: boolean): void {
    this.panelShadowStyle = scrolledOutOfView;
  }

  public onClear(): void {
    this.filterFormControl.patchValue('');
  }

  public updateUrl(search, madlibModel) {
    const filters = this.objectCache.cacheObject({ search, madlibModel });
    this.$state.go('.', { filters }, { notify: false, location: 'replace' });
  }

  public onMadlibChange(madlibOption: ITileSchoolTypeMadlibOptions) {
    this.madlibModel.patchValue(madlibOption);
  }

  public onTileClick({ to_focus_key, grouping_to_apply, filters_to_apply }, $stateParams): void {
    const { filters } = $stateParams;
    const tileData = this.objectCache.cacheObject({ to_focus_key, grouping_to_apply, filters_to_apply });
    this.$state.go('main.network.midlevel', { tileData, filters });
  }

  private getFilteredTilesData$(
    tileData: Array<ITileData>,
    input: string,
    madlibSelection: { key: string; human: string },
  ): Observable<Array<ITileData>> {
    return Observable.create((observer: Observer<Array<ITileData>>) => {
      const filteredByMadlib = this.applyMadlibFilter(tileData, madlibSelection);
      const filteredByInputAndMadlib = this.applyInputFilter(input, filteredByMadlib);
      observer.next(filteredByInputAndMadlib), observer.complete();
    });
  }

  private applyMadlibFilter(tileData: Array<ITileData>, madlibModel: { key: string; human: string }) {
    const { key } = madlibModel;
    if (key === 'ALL') return tileData;
    return tileData.filter((tile: ITileData) => tile[key]);
  }

  private applyInputFilter(input: string, tiles: Array<ITileData>): Array<ITileData> {
    return SortAndFilterService.filterTileData({ filterTerm: input, tileData: tiles });
  }

  private getTileSchoolTypes(tileData: Array<ITileData>): Array<TValidTileSchoolTypes> {
    const added: { ems?: boolean; hs?: boolean; hst?: boolean } = {};
    return tileData.reduce((acc, { ems, hs, hst }) => {
      if (ems && !added.ems) acc.push('ems'), (added.ems = true);
      if (hs && !added.hs) acc.push('hs'), (added.hs = true);
      if (hst && !added.hst) acc.push('hst'), (added.hst = true);
      return acc;
    }, []);
  }

  private getMadlibModel(madlibOptions: Array<ITileSchoolTypeMadlibOptions>): ITileSchoolTypeMadlibOptions {
    const [defaultMadlib] = sortBy(madlibOptions, ({ defaultHierarchy }) => defaultHierarchy);
    return defaultMadlib;
  }

  private getMadlibOptions(tileSchoolTypes: Array<TValidTileSchoolTypes>): Array<ITileSchoolTypeMadlibOptions> {
    const allOption = { key: 'ALL', human: 'All tiles', defaultHierarchy: 3 };
    const baseOptions = {
      ems: { key: 'ems', human: 'Elementary & middle school tiles', defaultHierarchy: 2 },
      hs: { key: 'hs', human: 'High school tiles', defaultHierarchy: 0 },
      hst: { key: 'hst', human: 'Transfer high school tiles', defaultHierarchy: 1 },
    };
    return tileSchoolTypes.reduce(
      (acc, schoolType: TValidTileSchoolTypes) => {
        acc.push(baseOptions[schoolType as TValidTileSchoolTypes]);
        return acc;
      },
      [allOption],
    );
  }
}
